<template>
  <div
    class="container"
    v-if="this.dependency"
    @dblclick="openComponentEditor()"
    @contextmenu="openContextMenu($event)"
    draggable
    @dragstart="startDrag($event, field)"
    @dragover.prevent
    @dragenter.prevent
    @drag="scrollOnDrag($event)"
    @drop.capture="onDrop($event)"
    :data-seq="field.layout.seq"
  >
    <div class="component">
      <component
        :is="`form-${this.field.metadata.type.toLowerCase()}`"
        :field="this.field"
        ref="component"
      ></component>
      <div class="buttons" v-if="!field.layout.inline">
        <button class="arrow-up" @click="moveUp()">&#9650;</button>
        <button class="arrow-down" @click="moveDown()">&#9660;</button>
      </div>
      <div class="buttons">
        <button class="arrow-up" @click="moveUp()" v-if="field.layout.inline">&#9650;</button>
        <button class="menu" @click="fireContextMenu($event)">&#10010;</button>
        <button class="delete" @click="deleteComponent()">X</button>
        <button class="arrow-down" @click="moveDown()" v-if="field.layout.inline">&#9660;</button>
      </div>
    </div>
  </div>
</template>

<script>
// FIELDS root node
import FormStatic from "./FormStatic.vue";
import FormStaticNT from "./FormStaticNT.vue";
import FormInputText from "./FormInputText.vue";
import FormCheckbox from "./FormCheckbox.vue";
import FormFile from "./FormFile.vue";
import FormOptionGroup from "./FormOptionGroup.vue";
import FormSelect from "./FormSelect.vue";
import FormPDICS from "./FormPDICS.vue";
// AREAS root node
import FormText from "./FormText.vue";
import FormPFSI from "./FormPFSI.vue";
import FormPFCP from "./FormPFCP.vue";
import FormPFRP from "./FormPFRP.vue";
import FormPFTS from "./FormPFTS.vue";
import FormPFHT from "./FormPFHT.vue";
import FormPFDS from "./FormPFDS.vue";
import FormPFRT from "./FormPFRT.vue";
import FormVFRT from "./FormVFRT.vue";
import FormPRCT from "./FormPRCT.vue";
import { VALIGN_MAP } from "../mixins/valign-mixin";
import { Bus } from "../main";
import { MESSAGES } from '../mixins/constant-mixin';

const MARGIN_BOTTOM_MUL = 4;

export default {
  name: "FormComponent",
  props: {
    field: {
      type: Object,
    },
    root: {
      type: String,
    },
  },
  components: {
    // FIELDS root node
    "form-static": FormStatic,
    "form-static_nt": FormStaticNT,
    "form-input_text": FormInputText,
    "form-checkbox": FormCheckbox,
    "form-file": FormFile,
    "form-option_group": FormOptionGroup,
    "form-select": FormSelect,
    "form-pdics": FormPDICS,
    // AREAS root node
    "form-text": FormText,
    "form-pfsi": FormPFSI,
    "form-pfcp": FormPFCP,
    "form-pfrp": FormPFRP,
    "form-pfts": FormPFTS,
    "form-pfht": FormPFHT,
    "form-pfds": FormPFDS,
    "form-pfrt": FormPFRT,
    "form-vfrt": FormVFRT,
    "form-prct": FormPRCT,
  },
  data() {
    return {
      // Initially: no dependency property = true
      dependency: !this.field.metadata.dependency, 
    };
  },
  mounted() {
    if (!this.dependency) {
      // emit from FormOptionGroup and FormSelect
      Bus.$on("checkDependency", this.checkDependency);
    }

    Bus.$on("updateDependency", (field) => {
      if (this.field.layout.seq !== field.layout.seq || !field.metadata.dependency) {
        return;
      }
      const parent = field.metadata.dependency.parent;
      const elem = document.querySelector(`select[name="${parent}"]`) 
        ? document.querySelector(`select[name="${parent}"]`) 
        : document.querySelector(`input[name="${parent}"]:checked`);
      this.dependency = !!field.metadata.dependency.displayIfValues.find(val => !!elem? val == elem.value: false);
      Bus.$on("checkDependency", this.checkDependency);
    })

    // emit from PopUpEditor
    Bus.$on("refreshLayout", (seq) => {
      if (this.field.layout.seq != seq) {
        return;
      }
      this.layoutInit();
    });

    //// Layout properties init and ignore not rendered component
    if (this.dependency) {
      this.layoutInit();
    }
  },
  beforeDestroy() {
    Bus.$off("checkDependency");
    Bus.$off("updateDependency");
    Bus.$off("refreshLayout");
  },
  methods: {
    layoutInit: function () {
      this.setClasses();
      this.setPadded();
      this.setBorder();
      this.setMarginBottom();
      this.setVerticalAlign();
      this.setInline();
      this.setColSize();
    },
    setClasses: function () {
      this.$refs.component.$el.classList = "";
      if (this.field.layout.className) {
        this.field.layout.className
          .replaceAll("\n", " ")
          .split(" ")
          .forEach((cls) => {
            this.$refs.component.$el.classList.add(cls);
          });
      }
    },
    setPadded: function () {
      if (this.field.layout.padded) {
        this.$refs.component.$el.classList.add("padded");
      } else {
        this.$refs.component.$el.classList.remove("padded");
      }
    },
    setBorder: function () {
      if (this.field.layout.border) {
        this.$refs.component.$el.classList.add("border");
      } else {
        this.$refs.component.$el.classList.remove("border");
      }
    },
    setMarginBottom: function () {
      if (this.field.layout.marginBottom) {
        this.$refs.component.$el.style.marginBottom = `${
          this.field.layout.marginBottom * MARGIN_BOTTOM_MUL
        }px`;
      } else {
        this.$refs.component.$el.style.marginBottom = 0;
      }
    },
    setVerticalAlign: function () {
      if (this.field.layout.verticalAlign) {
        const align = VALIGN_MAP[this.field.layout.verticalAlign.toLowerCase()];
        this.$refs.component.$el.style.verticalAlign = align;
      }
    },
    setInline: function () {
      if (this.field.layout.inline) {
        this.$el.classList.add("inline");
      } else {
        this.$el.classList.remove("inline");
      }
    },
    setColSize: function () {
      if (this.field.layout.colSize) {
        this.$el.style.width = `calc(${this.field.layout.colSize} * 100vw / 12)`;
      }
    },
    openComponentEditor: function () {
      Bus.$emit("openPopUp", this.field);

      // remove highlight after double clicking
      if (document.selection && document.selection.empty) {
        document.selection.empty();
      } else if (window.getSelection) {
        var sel = window.getSelection();
        sel.removeAllRanges();
      }
    },
    fireContextMenu: function (event) {
      const newEvent = new Event("contextmenu");
      newEvent.pageX = event.pageX;
      newEvent.pageY = event.pageY;
      this.$nextTick(() => this.$el.dispatchEvent(newEvent));
    },
    openContextMenu: function (event) {
      event.stopPropagation();
      this.$emit("openContextMenu", event);
    },
    startDrag: function (event, field) {
      event.dataTransfer.dropEffect = "move";
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("fieldSeq", field.layout.seq);
    },

    // situation 1 - drop the target element above the target element
    // situation 2 - drop the target element below the target element
    // take the position of cursor and the position of the target element
    // check whether the cursor is at the upper part or lower part of the target element
    // emit an event to FormEditor to arrange the element and change the seq
    onDrop: function (event) {
      event.stopPropagation();
      const draggingSeq = event.dataTransfer.getData("fieldSeq");
      const dropTarget = event.currentTarget;
      const targetSeq = dropTarget.dataset.seq;
      if (draggingSeq === targetSeq) {
        return;
      }

      const cursor = event.clientY;
      const rect = dropTarget.getBoundingClientRect();
      const centerLine = rect.bottom - rect.height / 2;
      if (cursor < centerLine) {
        this.$emit("setSeqAboveTarget", draggingSeq);
      } else {
        this.$emit("setSeqBelowTarget", draggingSeq);
      }
    },
    scrollOnDrag: function (event) {
      event.preventDefault();
      const eventHeight = event.clientY;
      const formEditor = this.$parent.$el;
      const rect = formEditor.getBoundingClientRect();
      const BOUND = 100;
      const upperBound = rect.top + BOUND;
      const lowerBound = rect.bottom - BOUND;
      if (eventHeight < upperBound) {
        formEditor.scrollTop -= 20;
      } else if (eventHeight > lowerBound) {
        formEditor.scrollTop += 20;
      }
    },
    deleteComponent: function () {
      if (!confirm(MESSAGES.DELETE_CONFIRMATION)) {
        return;
      }
      this.$emit("deleteField", this.field.layout.seq);
    },
    checkDependency: function () {
      if (!this.field.metadata.dependency) {
        this.dependency = true;
        return;
      }
      const dependency = this.field.metadata.dependency;
      const input = document.querySelector(`input[name="${dependency.parent}"]:checked`);
      const select = document.querySelector(`select[name="${dependency.parent}"]`);
      if (!input && !select) {
        this.dependency = false;
        return;
      }
      const value = input ? input.value : select.value;
      this.dependency = dependency.displayIfValues
        .map((e) => e.toString())
        .includes(value);
      if (this.dependency) {
        this.$nextTick(() => this.layoutInit());
      }
    },
    moveUp: function () {
      this.$emit("moveUp", this.field.layout.seq);
    },
    moveDown: function () {
      this.$emit("moveDown", this.field.layout.seq);
    },
  },
};
</script>

<style scoped>
.container {
  padding: 7px;
  border: 1px dashed #000;
}

.component {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.component > :first-child {
  width: 100%;
}

.buttons {
  display: flex;
  flex-direction: column;
}

.buttons button {
  margin-left: 0.5rem;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  box-sizing: border-box;
}

.menu {
  background: #5db75d;
  color: #fff;
  margin-bottom: 5px;
  padding: 1px 5px 2px 5px;
}

.delete {
  color: #fff;
  background: #ff6347;
  padding: 3px 5px 2px 5px;
}

.arrow-up {
  margin-bottom: 5px;
}

.arrow-down {
  margin-top: 5px;
}

.padded {
  padding: 1.5rem;
}

.border {
  border: 1px solid #000;
}

.inline {
  display: table-cell;
  width: 100vw;
}
</style>